import { Component } from '@theme/component';
import { getScrollTop } from '@theme/scroll-container';
import { push, EVENTS } from '@theme/analytics';

/**
 * The pill header: the desktop mega panels and the mobile drawer.
 *
 * Mega panels are disclosures (a button with `aria-expanded` and `aria-controls`), not menus,
 * so the links inside keep ordinary tab order. One opens at a time. Escape, a click outside,
 * or focus leaving the item closes it. On a fine pointer, hovering opens it after a short pause
 * and leaving closes it, so moving across the nav doesn't flash panels open.
 *
 * The mobile drawer sits in flow under the header (the design pushes the page down rather than
 * covering it), so it needs no scroll lock or focus trap.
 *
 * `site-header:open` / `site-header:close` still fire on `document` for the drawer, because the
 * shortlist bar listens for them to get out of the way.
 *
 * The header group is sticky. Scrolling down tucks it away and scrolling up brings it back, so it
 * is always one flick away without covering content. It stays put while a panel or the drawer is
 * open or focus is inside it. `--bk-header-offset` on the root carries its visible height, so
 * other bars fixed to the top (the category sub-nav) sit below it rather than under it.
 *
 * @typedef {Object} SiteHeaderRefs
 * @property {HTMLElement} bar
 * @property {HTMLElement} drawer
 * @property {HTMLButtonElement} toggle
 * @property {HTMLElement[]} [items]
 *
 * @extends {Component<SiteHeaderRefs>}
 */
class SiteHeaderComponent extends Component {
  requiredRefs = ['bar', 'drawer', 'toggle'];

  /** @type {number | undefined} */
  #hoverTimer;

  #hover = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');

  /** @type {HTMLElement | null} */
  #group = null;

  #lastScrollTop = 0;

  #scrollFrame = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocumentClick);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('click', this.#onNavClick);

    this.#group = this.closest('#header-group');
    this.#lastScrollTop = getScrollTop();
    this.#setHidden(false);
    // Scroll doesn't bubble, and the scrolling element changes at 990px (`.page-wrapper` on
    // desktop, the document on mobile), so listen in the capture phase for either.
    document.addEventListener('scroll', this.#onScroll, { capture: true, passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onDocumentClick);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.removeEventListener('click', this.#onNavClick);
    document.removeEventListener('scroll', this.#onScroll, { capture: true });
    window.clearTimeout(this.#hoverTimer);
    window.cancelAnimationFrame(this.#scrollFrame);
  }

  /**
   * Records which header link was followed and from where. Reads the clicked link only; the
   * navigation itself is untouched (GA4 sends with a beacon, so the page change doesn't drop it).
   * @param {MouseEvent} event
   */
  #onNavClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;

    let area = 'bar';
    if (link.matches('.site-header__wordmark')) area = 'logo';
    else if (link.matches('.site-header__cta, .site-header__drawer-cta')) area = 'cta';
    else if (link.closest('.site-header__mega')) area = 'mega';
    else if (this.refs.drawer.contains(link)) area = 'drawer';

    push(EVENTS.navClick, {
      nav_area: area,
      link_text: (link.textContent ?? '').replace(/\s+/g, ' ').replace('→', '').trim(),
      link_url: link.pathname,
    });
  };

  #onScroll = () => {
    if (this.#scrollFrame) return;
    this.#scrollFrame = window.requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      const top = getScrollTop();
      const delta = top - this.#lastScrollTop;
      // Ignore jitter; momentum scrolling and resize reflows produce single-pixel deltas.
      if (Math.abs(delta) < 6) return;
      this.#lastScrollTop = top;

      const busy = this.contains(document.activeElement) || this.querySelector('[aria-expanded="true"]');
      if (busy || top < this.offsetHeight) {
        this.#setHidden(false);
      } else {
        this.#setHidden(delta > 0);
      }
    });
  };

  /**
   * @param {boolean} hidden
   */
  #setHidden(hidden) {
    const group = this.#group;
    if (!group) return;
    group.toggleAttribute('data-header-hidden', hidden);
    document.documentElement.style.setProperty('--bk-header-offset', hidden ? '0px' : `${group.offsetHeight}px`);
  }

  /**
   * @param {Event} event
   */
  toggleMega(event) {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    const isOpen = button.getAttribute('aria-expanded') === 'true';
    this.#closeAllMegas();
    if (!isOpen) this.#openMega(button);
  }

  /**
   * @param {PointerEvent} event
   */
  hoverOpen(event) {
    if (!this.#hover.matches || event.pointerType !== 'mouse') return;
    const item = /** @type {HTMLElement} */ (event.target);
    window.clearTimeout(this.#hoverTimer);
    this.#hoverTimer = window.setTimeout(() => {
      const button = item.querySelector('button[aria-controls]');
      if (!(button instanceof HTMLButtonElement)) return;
      if (button.getAttribute('aria-expanded') === 'true') return;
      this.#closeAllMegas();
      this.#openMega(button);
    }, 80);
  }

  /**
   * @param {PointerEvent} event
   */
  hoverClose(event) {
    if (!this.#hover.matches || event.pointerType !== 'mouse') return;
    window.clearTimeout(this.#hoverTimer);
    this.#hoverTimer = window.setTimeout(() => this.#closeAllMegas(), 160);
  }

  toggleDrawer() {
    const { drawer, toggle } = this.refs;
    const open = drawer.hidden;
    drawer.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    toggle.querySelector('.site-header__toggle-open')?.toggleAttribute('hidden', open);
    toggle.querySelector('.site-header__toggle-close')?.toggleAttribute('hidden', !open);
    document.dispatchEvent(new CustomEvent(open ? 'site-header:open' : 'site-header:close'));
  }

  /**
   * @param {HTMLButtonElement} button
   */
  #openMega(button) {
    const panel = this.#panelFor(button);
    if (!panel) return;
    panel.hidden = false;
    button.setAttribute('aria-expanded', 'true');
  }

  #closeAllMegas() {
    for (const button of this.querySelectorAll('.site-header__nav button[aria-expanded="true"]')) {
      const panel = this.#panelFor(/** @type {HTMLButtonElement} */ (button));
      if (panel) panel.hidden = true;
      button.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * @param {HTMLButtonElement} button
   * @returns {HTMLElement | null}
   */
  #panelFor(button) {
    const id = button.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const open = this.querySelector('.site-header__nav button[aria-expanded="true"]');
    if (open instanceof HTMLButtonElement) {
      this.#closeAllMegas();
      open.focus();
    }
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!(event.target instanceof Node)) return;
    const item = /** @type {Element} */ (event.target).closest?.('.site-header__item');
    if (item && this.contains(item)) return;
    this.#closeAllMegas();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (!(next instanceof Node)) return;
    const item = /** @type {HTMLElement} */ (event.target).closest('.site-header__item');
    if (item && !item.contains(next)) this.#closeAllMegas();
  };
}

if (!customElements.get('site-header-component')) {
  customElements.define('site-header-component', SiteHeaderComponent);
}
